import { FormEvent, ReactElement, ReactNode, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Fieldset, Heading, Paragraph, Radio, Skeleton, Tag, ValidationMessage } from '@digdir/designsystemet-react';
import { AuthLayout } from '../../components/AuthLayout';
import type { OnboardingGoal, OnboardingRole, UserOnboarding } from '../../api/userOnboarding';
import { useUpdateUserOnboardingMutation, useUserOnboardingQuery } from '../../hooks/queries/onboarding/useUserOnboarding';
import { Routes } from '../../enums/Routes';
import { Perks } from '../SignUp/Perks';
import { GOALS, homeFor, ROLES } from './gettingStartedView';
import classes from './GettingStarted.module.css';

const K = 'gettingStarted';

/** «Valgene dine», with «Endre» for each choice made so far */
const Choices = ({ onboarding }: { onboarding: UserOnboarding }): ReactElement | null => {
  const { t } = useTranslation();
  const rows: Array<{ label: string; value: string; to?: string }> = [];
  if (onboarding.companyName) rows.push({ label: t(`${K}.choices.company`), value: onboarding.companyName });
  if (onboarding.role) rows.push({ label: t(`${K}.choices.role`), value: t(`${K}.roles.${onboarding.role}.label`), to: Routes.OnboardingRole });
  if (onboarding.goal) rows.push({ label: t(`${K}.choices.goal`), value: t(`${K}.goals.${onboarding.goal}.label`), to: Routes.OnboardingGoal });
  if (rows.length === 0) return null;
  return (
    <section className={classes.choices} aria-labelledby="onboarding-choices">
      <Heading level={2} data-size="2xs" id="onboarding-choices">
        {t(`${K}.choices.title`)}
      </Heading>
      <dl>
        {rows.map((row) => (
          <div key={row.label}>
            <dt>{row.label}</dt>
            <dd>
              <span>{row.value}</span>
              {row.to && (
                <Link to={row.to} className="ds-link" aria-label={t(`${K}.choices.change`, { what: row.label.toLowerCase() })}>
                  {t(`${K}.choices.changeShort`)}
                </Link>
              )}
            </dd>
          </div>
        ))}
      </dl>
      <Paragraph data-size="xs" className={classes.subtle}>
        {t(`${K}.choices.saved`)}
      </Paragraph>
    </section>
  );
};

/** The steps after the code: one card with the choice, and what you get and have chosen beside it */
const ChoiceLayout = ({
  title,
  lead,
  onboarding,
  children,
}: {
  title: string;
  lead: string;
  onboarding: UserOnboarding | undefined;
  children: ReactNode;
}): ReactElement => (
  <AuthLayout
    wide
    title={title}
    lead={lead}
    aside={
      <div className={classes.aside}>
        <Perks />
        {onboarding && <Choices onboarding={onboarding} />}
      </div>
    }
  >
    {children}
  </AuthLayout>
);

/** A choice as a card: the radio, a label and what it means */
const Option = ({
  name,
  value,
  checked,
  onChange,
  label,
  description,
  extra,
}: {
  name: string;
  value: string;
  checked: boolean;
  onChange: () => void;
  label: string;
  description: string;
  extra?: ReactNode;
}): ReactElement => (
  <div className={classes.option} data-checked={checked || undefined}>
    <Radio
      name={name}
      value={value}
      checked={checked}
      onChange={onChange}
      label={
        <span className={classes.optionLabel}>
          {label}
          {extra}
        </span>
      }
      description={description}
    />
  </div>
);

const Loading = (): ReactElement => <Skeleton variant="rectangle" width="100%" height={320} />;

/** «Hva er rollen din?» The role decides what comes first. Access is full, as the user is administrator. */
export const RolePage = (): ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data, isLoading } = useUserOnboardingQuery();
  const save = useUpdateUserOnboardingMutation();
  const [role, setRole] = useState<OnboardingRole | null>(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    if (data?.role && role === null) setRole(data.role);
  }, [data, role]);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!role) return setMissing(true);
    save.mutate(
      { role },
      {
        onSuccess: () => {
          // The roles changed, so the menu must be built again
          void queryClient.invalidateQueries({ queryKey: ['GET_USERINFO'] });
          navigate(Routes.OnboardingGoal);
        },
      },
    );
  };

  return (
    <ChoiceLayout title={t(`${K}.role.title`)} lead={t(`${K}.role.lead`)} onboarding={data}>
      {isLoading ? (
        <Loading />
      ) : (
        <form className={classes.form} onSubmit={submit} noValidate>
          <Fieldset>
            <Fieldset.Legend className="ds-sr-only">{t(`${K}.role.title`)}</Fieldset.Legend>
            <div className={classes.options}>
              {ROLES.map((option) => (
                <Option
                  key={option}
                  name="onboarding-role"
                  value={option}
                  checked={role === option}
                  onChange={() => {
                    setRole(option);
                    setMissing(false);
                  }}
                  label={t(`${K}.roles.${option}.label`)}
                  description={t(`${K}.roles.${option}.description`)}
                />
              ))}
            </div>
            {missing && <ValidationMessage>{t(`${K}.role.missing`)}</ValidationMessage>}
          </Fieldset>
          {save.isError && (
            <Alert data-color="danger" data-size="sm">
              <Paragraph data-size="sm">{t(`${K}.failed`)}</Paragraph>
            </Alert>
          )}
          <Button type="submit" className={classes.submit} loading={save.isPending}>
            {t(`${K}.role.continue`)}
          </Button>
        </form>
      )}
    </ChoiceLayout>
  );
};

/** «Hva vil du få gjort først?» One place to start. The role's goal is recommended. */
export const GoalPage = (): ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading } = useUserOnboardingQuery();
  const save = useUpdateUserOnboardingMutation();
  const [goal, setGoal] = useState<OnboardingGoal | null>(null);

  useEffect(() => {
    if (!data || goal !== null) return;
    const start = data.goal ?? data.recommendedGoal;
    if (start && GOALS.includes(start)) setGoal(start);
  }, [data, goal]);

  // The role comes first: without it there is nothing to recommend
  useEffect(() => {
    if (data && !data.role) navigate(Routes.OnboardingRole, { replace: true });
  }, [data, navigate]);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!goal) return;
    // Into the app, on the page the goal is worked on, where «Kom i gang» shows the first step
    save.mutate({ goal }, { onSuccess: () => navigate(homeFor(goal) ?? Routes.Dashboard) });
  };

  return (
    <ChoiceLayout title={t(`${K}.goal.title`)} lead={t(`${K}.goal.lead`)} onboarding={data}>
      {isLoading ? (
        <Loading />
      ) : (
        <form className={classes.form} onSubmit={submit} noValidate>
          <Fieldset>
            <Fieldset.Legend className="ds-sr-only">{t(`${K}.goal.title`)}</Fieldset.Legend>
            <div className={classes.options}>
              {GOALS.map((option) => (
                <Option
                  key={option}
                  name="onboarding-goal"
                  value={option}
                  checked={goal === option}
                  onChange={() => setGoal(option)}
                  label={t(`${K}.goals.${option}.label`)}
                  description={t(`${K}.goals.${option}.description`)}
                  extra={
                    data?.recommendedGoal === option ? (
                      <Tag data-color="success" data-size="sm">
                        {t(`${K}.goal.recommended`)}
                      </Tag>
                    ) : undefined
                  }
                />
              ))}
            </div>
          </Fieldset>
          {save.isError && (
            <Alert data-color="danger" data-size="sm">
              <Paragraph data-size="sm">{t(`${K}.failed`)}</Paragraph>
            </Alert>
          )}
          <div className={classes.actions}>
            <Button type="submit" loading={save.isPending}>
              {t(`${K}.goal.go`)}
            </Button>
            <Button asChild variant="tertiary">
              <Link to={Routes.OnboardingRole}>{t(`${K}.back`)}</Link>
            </Button>
          </div>
        </form>
      )}
    </ChoiceLayout>
  );
};
